"use client";

import React, { useState, useRef, ReactNode } from "react";
import Link from "next/link";
import Image from "next/image";
import { useCart } from "../data/CartContext";
import { track } from "../lib/track";
import { BrandMicrodata, OfferPoliciesMicrodata } from "./ProductMicrodata";

type TrustItem = {
  label: string;          // e.g., "Secure payment", "Ships in 48h"
  icon?: ReactNode;       // e.g., <ShieldCheck />, "🔒"
};

interface ProductCardProps {
  imageSrc: string;
  title: string;
  price: string;          // "£10.00"
  size: string;
  slug: string;
  id: string;             // sku
  ingredients: string[] | string; // short list of key ingredients; bundles give one entry per product

  // Structured data only — the card shows `tagline`, but Google's merchant
  // listings report wants a `description` on every Product. `metaDescription`
  // is the short, product-specific line; the long `description` is the
  // fallback, and is what the detail page publishes.
  description?: string;
  metaDescription?: string;

  // optional enhancements
  tagline?: string;       // short benefit line
  badge?: string;         // "Best Seller"
  rating?: number;        // 0..5
  reviewCount?: number;

  // NEW: trust pulled from data
  trust?: TrustItem[];    // up to 3 items; per product

  reviews?: { author: string; text: string }[];
}

const Stars: React.FC<{ rating: number }> = ({ rating }) => {
  const full = Math.floor(rating);
  const half = rating - full >= 0.5;
  const empty = 5 - full - (half ? 1 : 0);
  return (
    <span aria-label={`${rating} out of 5 stars`} className="text-sm leading-none">
      {"★".repeat(full)}{half ? "☆" : ""}{"✩".repeat(empty)}
    </span>
  );
};

const ProductCard: React.FC<ProductCardProps> = ({
  imageSrc,
  title,
  price,
  size,
  slug,
  id,
  tagline,
  badge,
  rating,
  reviewCount,
  ingredients,
  description,
  metaDescription,
  trust = [],
  reviews = [],
}) => {
  const { addToCart } = useCart();
  const [showNote, setShowNote] = useState(false);
  const clickedRef = useRef(false);

  // `price` is a display string ("£30.00 GBP"). Schema.org needs a bare number,
  // so parse once: NaN means "don't emit a price" (the cart keeps its 0 fallback).
  const parsedPrice = Number(String(price).replace(/[^\d.-]/g, ""));
  const numericPrice = parsedPrice || 0;

  const handleAddToCart = () => {
    addToCart({ id, title, price, qty: 1, totalPrice: numericPrice });
    track(
      "add_to_cart", // Event name
      {
        label: `Added 1 × ${title}`, // Metadata: label
        component: "ProductCard", // Metadata: component
      },
      {
        event: "add_to_cart", // Event name for TrackEvent
        label: `Added 1 × ${title}`, // Event label
        component: "ProductCard", // Component name
        meta: {
          timestamp: Date.now(), // Optional: Add a timestamp for the event
        },
      }
    );
    setShowNote(true);
    setTimeout(() => setShowNote(false), 4000);
  };

  const handleSeeDetails = () => {
    if (clickedRef.current) return;
    clickedRef.current = true;
    track(
      "see_more", // Event name
      {
        label: `See details: ${title}`, // Metadata: label
        component: "ProductCard", // Metadata: component
      },
      {
        event: "see_more", // Event name for TrackEvent
        label: `See details: ${title}`, // Event label
        component: "ProductCard", // Component name
        meta: {
          timestamp: Date.now(), // Optional: Add a timestamp for the event
        },
      }
    );
    setTimeout(() => { clickedRef.current = false; }, 1200);
  };

  const handleImageView = () => {
    if (clickedRef.current) return;
    clickedRef.current = true;
    setTimeout(() => { clickedRef.current = false; }, 1200);
  };

  // Badge styles by label — distinct shape & color per badge
  const badgeStyles: Record<string, string> = {
    "Best Seller": "bg-amber-500 text-white rounded-full",
    "Top Pick": "bg-green-700 text-white rounded-lg",
    "Limited Batch": "bg-teal-500 text-white rounded-md",
    "100% Natural": "bg-emerald-700 text-white rounded-xl",
  };

  return (
    <article
      className="bg-[var(--color-bg-muted)] shadow-lg rounded-2xl p-5 text-center flex flex-col transition hover:shadow-xl hover:-translate-y-0.5"
      itemScope
      itemType="https://schema.org/Product"
    >
      <meta itemProp="sku" content={id} />
      {(metaDescription || description) && (
        <meta itemProp="description" content={metaDescription || description} />
      )}
      <BrandMicrodata />

      {/* 1) Image → PDP */}
      <Link href={`/shop/${slug}`} onClick={handleImageView} className="relative block group" itemProp="url">
        <figure className="relative w-full h-52 overflow-hidden rounded-xl">
          {badge && (
            <span
              className={`absolute top-2 left-2 text-xs px-2 py-1 shadow font-semibold overflow-hidden select-none z-10
                transition-transform duration-200 will-change-transform
                group-hover:scale-105 group-hover:shadow-md
                ${badgeStyles[badge] || "bg-[var(--color-brand)] text-white rounded-md"}`}
            >
              {/* subtle shine on hover */}
              <span
                aria-hidden
                className="pointer-events-none absolute inset-0 -translate-x-full group-hover:translate-x-full bg-white/20 skew-x-12 transition-transform duration-700"
              />
              {badge}
            </span>
          )}
          <Image
            src={imageSrc}
            alt={`${title} – ${size}`}
            fill
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
            className="object-cover rounded-xl"
          />
          <meta itemProp="image" content={imageSrc} />
        </figure>
      </Link>

      {/* 2) Name + benefit */}
      <h2 className="mt-3 text-lg font-semibold text-[var(--color-text-brand)] leading-tight" itemProp="name">
        {title}
      </h2>
      {tagline && (
        <p className="text-sm text-[var(--color-text-secondary)] mt-1 leading-relaxed">{tagline}</p>
      )}

      {/* Optional ratings */}
      {(typeof rating === "number" && typeof reviewCount === "number") && (
        <div className="mt-2 flex items-center justify-center gap-2 text-gray-700">
          <Stars rating={rating} />
          <span className="text-xs">{rating.toFixed(1)} · {reviewCount} reviews</span>
          <span itemProp="aggregateRating" itemScope itemType="https://schema.org/AggregateRating">
            <meta itemProp="ratingValue" content={String(rating)} />
            <meta itemProp="reviewCount" content={String(reviewCount)} />
          </span>
        </div>
      )}

      {/* 3) Price near CTA */}
      <p
        className="mt-3 text-base font-medium text-[var(--color-text-brand)]"
        itemProp="offers"
        itemScope
        itemType="https://schema.org/Offer"
      >
        <meta itemProp="priceCurrency" content="GBP" />
        {!Number.isNaN(parsedPrice) && (
          <meta itemProp="price" content={parsedPrice.toFixed(2)} />
        )}
        <span>{price}</span>
        <meta itemProp="availability" content="https://schema.org/InStock" />
        <OfferPoliciesMicrodata price={Number.isNaN(parsedPrice) ? null : parsedPrice} />
      </p>

      {/* 4) Primary + secondary CTAs */}
      <div className="mt-4 flex items-center justify-center gap-3">
        <button
          onClick={handleAddToCart}
          className="bg-[var(--color-text-brand)] text-white py-2 px-4 rounded-md hover:bg-[var(--color-text-brand-contrast)] transition font-medium"
        >
          Add to Cart
        </button>
        <Link
          href={`/shop/${slug}`}
          onClick={handleSeeDetails}
          className="text-sm text-[var(--color-text-brand)] underline underline-offset-4 hover:text-[var(--color-brand)]/50 opacity-90"
        >
          See details →
        </Link>
      </div>

    {/* 5) Trust strip (from data) */}
    {trust.length > 0 && (
      <ul className="mt-4 text-xs text-gray-700 hidden md:grid grid-cols-1 gap-y-2 mx-auto text-left">
        {trust.slice(0, 3).map((t, i) => (
          <li key={i} className="flex items-start gap-2">
            <span aria-hidden className="mt-[1px] text-[14px] shrink-0">
              {t.icon ?? "✅"}
            </span>
            <span className="leading-snug break-words">{t.label}</span>
          </li>
        ))}
      </ul>
    )}

      {/* 6) Real customer reviews */}
      {reviews.length > 0 && (
        <div className="mt-4 space-y-2">
          {reviews.slice(0, 2).map((r, i) => (
            <blockquote key={i} className="text-xs text-gray-600 italic border-l-2 border-[var(--color-brand)] pl-2 text-left">
              <p className="leading-snug">&ldquo;{r.text}&rdquo;</p>
              <footer className="mt-1 not-italic font-medium text-gray-500">— {r.author}</footer>
            </blockquote>
          ))}
        </div>
      )}

      {/* 7) Meta / short info */}
      <p className="mt-3 text-xs text-gray-500">Pack Size: {size}</p>
      <p className="text-xs mt-2 text-gray-600 mt-1 line-clamp-2 hidden md:block" itemProp="ingredients">{Array.isArray(ingredients) ? ingredients.join(" · ") : ingredients}</p>

      {/* Confirmation note */}
      {showNote && (
        <div className="mt-3 text-sm bg-green-50 border border-[var(--color-accent)] rounded-lg py-2 px-3 text-[var(--color-accent)]">
          Added <strong>{title}</strong> to cart.{" "}
          <Link href="/shop/cart" className="underline text-[var(--color-accent)]">View cart →</Link>
        </div>
      )}

      {/* Local styles for any fine-tuned keyframes if you want to elevate later */}
      <style jsx>{`
        /* reserved for any future custom animations */
      `}</style>
    </article>
  );
};

export default ProductCard;
